import { StandardEvents } from '@shopify/events';
import { deliveryRange, readSaleContract, saleState } from '@artsy/product-timing-core';
import { saleNow, syncSaleClock } from '@artsy/sale-clock';

class ArtsyProductTiming extends HTMLElement {
  #controller; #timer; #copy; #offer; #context; #pending;
  #json = selector => { try { return JSON.parse(this.querySelector(selector)?.textContent || 'null'); } catch { return null; } };
  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#copy = this.#json('[data-timing-copy]') || {};
    this.#readSale(this);
    const options = {signal:this.#controller.signal};
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#select, options);
    document.addEventListener('visibilitychange', this.#visibility, options);
    window.addEventListener('pageshow', this.#syncClock, options);
    this.#render();
    this.#syncClock();
    this.#schedule();
  }
  disconnectedCallback() {
    this.#controller?.abort();
    clearInterval(this.#timer);
  }
  #readSale = element => {
    this.#offer = null;
    this.#context = null;
    try {
      const context = JSON.parse(element.querySelector('[data-sale-context]')?.textContent || 'null');
      if (!context || context.product_id !== element.dataset.productId || context.variant_id !== element.dataset.variantId ||
          context.available !== (element.dataset.available === 'true')) return;
      this.#context = context;
      this.#offer = readSaleContract(element.querySelector('[data-sale-contract]')?.textContent,
        element.querySelector('[data-sale-product-prices]')?.textContent, context);
    } catch { this.#context = null; }
  };
  #schedule = () => {
    clearInterval(this.#timer);
    this.#timer = setInterval(this.#tick, this.#offer?.enabled === true ? 1000 : 60000);
  };
  #visibility = () => { if (!document.hidden) this.#syncClock(); };
  #tick = () => {
    if (document.hidden) return;
    this.#syncClock();
    this.#render();
  };
  #syncing = false;
  #syncClock = async () => {
    if (this.#syncing || !this.isConnected) return;
    this.#syncing = true;
    try {
      await syncSaleClock(this.dataset.productUrl);
    }
    finally {
      this.#syncing = false;
      if (this.isConnected) this.#render();
    }
  };
  #render = () => {
    const node = this.querySelector('[data-delivery-result]');
    // The US market uses the approved standard estimate; this is not address detection.
    const region = this.dataset.country === 'US' ? 'contiguous' : 'other';
    const available = this.dataset.available === 'true' && !this.#pending;
    const now = saleNow();
    // Delivery is a calendar estimate; only the sale requires the verified HTTP clock.
    const range = deliveryRange(now ?? Date.now(), region, available);
    let message = this.#copy.calendar_unavailable;
    if (!available) message = this.#copy.unavailable;
    else if (region === 'other') message = this.#copy.other;
    else if (range) {
      const dates = range.map(d => new Date(d + 'T12:00:00Z'));
      const year = range[0].slice(0, 4) !== range[1].slice(0, 4) ? 'numeric' : undefined;
      const format = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {month:'short',day:'numeric',year,timeZone:'UTC'});
      message = this.#copy.get_it?.replace('{range}', format.formatRange(...dates));
    }
    if (node && node.textContent !== message) node.textContent = message || '';
    const sale = this.querySelector('[data-sale]'), line = this.querySelector('[data-sale-line]');
    const state = saleState(this.#offer, {...this.#context, available}, now);
    if (!sale || !line) return;
    sale.hidden = state.state === 'held' || !Number.isFinite(state.remainingSeconds) || state.remainingSeconds <= 0;
    this.dataset.saleState = state.state;
    if (!sale.hidden && (state.state === 'active' || state.state === 'off')) {
      const n = state.remainingSeconds;
      const time = `${Math.floor(n/3600)}:${String(Math.floor(n/60)%60).padStart(2,'0')}:${String(n%60).padStart(2,'0')}`;
      const label = state.state === 'active' ? this.#copy.sale_ends : this.#copy.sale_starts;
      line.textContent = label?.replace('{time}', time) || '';
    } else line.textContent = '';
  };
  #select = event => {
    const picker = event.target?.closest?.('variant-picker');
    if (!picker || picker.closest('product-card') || picker.dataset.productId !== this.dataset.productId || !event.promise?.then) return;
    const token = {};
    this.#pending = token;
    this.#render();
    event.promise.then(({detail}) => {
      if (!this.isConnected || this.#pending !== token) return;
      const incoming = [...(detail?.html?.querySelectorAll?.('artsy-product-timing') || [])].find(el =>
        el.dataset.productId === this.dataset.productId && el.dataset.blockId === this.dataset.blockId);
      if (!incoming || String(detail?.resource?.id) !== incoming.dataset.variantId) throw new Error('Unconfirmed variant');
      this.dataset.variantId = incoming.dataset.variantId;
      this.dataset.available = incoming.dataset.available;
      this.#readSale(incoming);
      this.#pending = null;
      this.#schedule();
      this.#render();
    }).catch(() => {
      if (this.#pending !== token) return;
      this.dataset.available = 'false';
      this.#pending = null;
      this.#render();
    });
  };
}
if (!customElements.get('artsy-product-timing')) customElements.define('artsy-product-timing', ArtsyProductTiming);
